import { useState, useEffect, useRef } from 'react'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Activity,
  Pill,
  FlaskConical,
  Users,
  Loader2,
  BarChart3,
} from 'lucide-react'
import { cn } from '@lib/utils'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { api } from '@services/api'
import type { Patient } from '@/types'
import { pharmacyApi } from '@services/pharmacy.service'

interface PrescriptionMetricDetail {
  id: string
  prescribed_date: string | null
  dosage: string
  quantity: number
  status: string | null
}

interface PrescriptionMetric {
  medication_id: string
  medication_name: string
  total_quantity: number
  total_prescriptions: number
  first_prescribed: string | null
  last_prescribed: string | null
  active_prescriptions: number
  details: {
    current_dosage: string | null
    current_frequency: string | null
    status: string
    refills_remaining: number
    prescriptions: PrescriptionMetricDetail[]
  } | null
}

interface HistoriaClinicaPanelProps {
  patientId: string
  /** Lay the sections out side by side (a row) instead of stacked vertically. */
  horizontal?: boolean
}

const prescriptionStatusLabels: Record<string, string> = {
  active: 'Activa',
  expired: 'Expirada',
  filled: 'Surtida',
  picked_up: 'Entregada',
  cancelled: 'Cancelada',
  pending: 'Pendiente',
  prescribed: 'Recetada',
}

const labStatusLabels: Record<string, string> = {
  ordered: 'Solicitado',
  collected: 'Tomado',
  in_progress: 'En proceso',
  completed: 'Completado',
  reviewed: 'Revisado',
  cancelled: 'Cancelado',
}

function SectionHeader({
  icon: Icon,
  title,
  count,
  isOpen,
  onToggle,
}: {
  icon: React.ElementType
  title: string
  count?: number
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full min-w-0 flex items-center gap-2 py-2 px-1 text-left hover:bg-ml-surface-container-low/50 rounded-lg transition-colors"
    >
      {isOpen ? (
        <ChevronDown className="h-3.5 w-3.5 text-ml-on-surface-variant/60 flex-shrink-0" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5 text-ml-on-surface-variant/60 flex-shrink-0" />
      )}
      <Icon className="h-4 w-4 text-ml-primary flex-shrink-0" />
      <span className="text-xs font-bold text-ml-on-surface uppercase tracking-wider flex-1 min-w-0 break-words leading-tight">
        {title}
      </span>
      {count !== undefined && count > 0 && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-ml-primary-fixed text-ml-primary font-bold">
          {count}
        </span>
      )}
    </button>
  )
}

export function HistoriaClinicaPanel({ patientId, horizontal = false }: HistoriaClinicaPanelProps) {
  const { formatDate } = useClinicFormat()
  const [patient, setPatient] = useState<Patient | null>(null)
  const [prescriptionMetrics, setPrescriptionMetrics] = useState<PrescriptionMetric[]>([])
  const [labResults, setLabResults] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Section toggles. In horizontal mode the panel is a compact header row, so
  // every section starts collapsed (titles only — expand on click).
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    allergies: !horizontal,
    chronic: !horizontal,
    medications: !horizontal,
    prescriptionMetrics: false,
    consultations: false,
    labs: false,
    family: false,
  })

  // Expanded prescription metric rows
  const [expandedMed, setExpandedMed] = useState<string | null>(null)

  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))

  useEffect(() => {
    if (!patientId) return

    const loadData = async () => {
      setLoading(true)
      try {
        const [patientRes, metricsRes, labRes] = await Promise.all([
          api.get<Patient>(`/patients/${patientId}`),
          api.get<{ medications: PrescriptionMetric[] }>(
            `/pharmacy/prescriptions/metrics/patient/${patientId}`
          ).catch(() => ({ medications: [] })),
          pharmacyApi.getLabResults({ patient_id: patientId, limit: 20 }).catch(() => ({ items: [] })),
        ])
        setPatient(patientRes)
        setPrescriptionMetrics(metricsRes.medications)
        setLabResults(labRes.items ?? [])
      } catch (err) {
        console.error('Failed to load historia clinica:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [patientId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-ml-primary" />
      </div>
    )
  }

  if (!patient) return null

  const allergies = patient.allergies ?? []
  const chronicConditions = patient.chronic_conditions ?? []
  const currentMedications = patient.current_medications ?? []
  const familyHistory = patient.family_history ?? []

  return (
    <div
      className={
        horizontal
          ? 'grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-3 text-sm items-start'
          : 'space-y-1 text-sm pt-2'
      }
    >
      {/* Allergies */}
      <div
        ref={(node) => {
          sectionRefs.current.allergies = node
        }}
      >
        <SectionHeader
          icon={AlertTriangle}
          title="Alergias"
          count={allergies.length}
          isOpen={openSections.allergies}
          onToggle={() => toggleSection('allergies')}
        />
        {openSections.allergies && (
          <div className="pl-7 pr-1 pb-2 space-y-2">
            {allergies.length === 0 ? (
              <p className="text-xs text-ml-on-surface-variant/60 italic">
                Sin alergias registradas
              </p>
            ) : (
              allergies.map((a, i) => {
                const severityColor =
                  a.severity === 'severe'
                    ? 'border-l-ml-error bg-ml-error/5'
                    : a.severity === 'moderate'
                      ? 'border-l-orange-500 bg-orange-500/5'
                      : 'border-l-yellow-500 bg-yellow-500/5'
                const severityBadge =
                  a.severity === 'severe'
                    ? 'bg-ml-error/10 text-ml-error'
                    : a.severity === 'moderate'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-yellow-100 text-yellow-700'
                const severityLabel =
                  a.severity === 'severe'
                    ? 'Severa'
                    : a.severity === 'moderate'
                      ? 'Moderada'
                      : 'Leve'
                const typeLabel: Record<string, string> = {
                  drug: 'Medicamento',
                  food: 'Alimento',
                  environmental: 'Ambiental',
                  other: 'Otro',
                }
                return (
                  <div
                    key={i}
                    className={cn(
                      'border-l-2 rounded-r-md px-2.5 py-2',
                      severityColor
                    )}
                  >
                    {/* Name + severity */}
                    <div className="flex items-start justify-between gap-2">
                      <p className="flex-1 text-xs font-semibold text-ml-on-surface break-words leading-snug">
                        {a.name}
                      </p>
                      <span
                        className={cn(
                          'flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide',
                          severityBadge
                        )}
                      >
                        {severityLabel}
                      </span>
                    </div>

                    {/* Type */}
                    <span className="mt-1.5 inline-block text-[9px] px-1.5 py-0.5 rounded bg-ml-surface-container-high text-ml-on-surface-variant uppercase tracking-wide">
                      {typeLabel[a.type] ?? 'Otro'}
                    </span>

                    {/* Reaction */}
                    {a.reaction && (
                      <p className="mt-1.5 text-[10px] text-ml-on-surface-variant/80 break-words leading-snug">
                        <span className="text-ml-on-surface-variant/50">Reacción: </span>
                        {a.reaction}
                      </p>
                    )}
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>

      {/* Chronic Conditions */}
      <div
        ref={(node) => {
          sectionRefs.current.chronic = node
        }}
      >
        <SectionHeader
          icon={Activity}
          title="Condiciones Crónicas"
          count={chronicConditions.length}
          isOpen={openSections.chronic}
          onToggle={() => toggleSection('chronic')}
        />
        {openSections.chronic && (
          <div className="pl-7 pb-2 space-y-1">
            {chronicConditions.length === 0 ? (
              <p className="text-xs text-ml-on-surface-variant/60 italic">
                Sin condiciones crónicas
              </p>
            ) : (
              chronicConditions.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ml-primary flex-shrink-0" />
                  <span className="text-xs text-ml-on-surface">{c}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Current Medications */}
      <div
        ref={(node) => {
          sectionRefs.current.medications = node
        }}
      >
        <SectionHeader
          icon={Pill}
          title="Tratamientos Actuales"
          count={currentMedications.length}
          isOpen={openSections.medications}
          onToggle={() => toggleSection('medications')}
        />
        {openSections.medications && (
          <div className="pl-7 pb-2 space-y-1">
            {currentMedications.length === 0 ? (
              <p className="text-xs text-ml-on-surface-variant/60 italic">
                Sin medicamentos actuales
              </p>
            ) : (
              currentMedications.map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
                  <span className="text-xs text-ml-on-surface">{m}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Prescription Metrics */}
      <div>
        <SectionHeader
          icon={BarChart3}
          title="Métricas de Recetas"
          count={prescriptionMetrics.length}
          isOpen={openSections.prescriptionMetrics}
          onToggle={() => toggleSection('prescriptionMetrics')}
        />
        {openSections.prescriptionMetrics && (
          <div className="pl-7 pb-2 space-y-1.5">
            {prescriptionMetrics.length === 0 ? (
              <p className="text-xs text-ml-on-surface-variant/60 italic">
                Sin recetas registradas
              </p>
            ) : (
              prescriptionMetrics.map((metric) => (
                <div key={metric.medication_id}>
                  {/* Collapsed row */}
                  <button
                    onClick={() =>
                      setExpandedMed(
                        expandedMed === metric.medication_id
                          ? null
                          : metric.medication_id
                      )
                    }
                    className="w-full text-left p-2 rounded-lg hover:bg-ml-surface-container-low/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-ml-on-surface truncate">
                        {metric.medication_name}
                      </span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-ml-on-surface-variant">
                          {metric.total_quantity} uds
                        </span>
                        {expandedMed === metric.medication_id ? (
                          <ChevronDown className="h-3 w-3 text-ml-on-surface-variant/60" />
                        ) : (
                          <ChevronRight className="h-3 w-3 text-ml-on-surface-variant/60" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-ml-on-surface-variant/60">
                        {metric.total_prescriptions} recetas
                      </span>
                      {metric.first_prescribed && metric.last_prescribed && (
                        <>
                          <span className="text-[10px] text-ml-on-surface-variant/40">·</span>
                          <span className="text-[10px] text-ml-on-surface-variant/60">
                            {new Date(metric.first_prescribed).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' })}
                            {' → '}
                            {new Date(metric.last_prescribed).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' })}
                          </span>
                        </>
                      )}
                    </div>
                  </button>

                  {/* Expanded details */}
                  {expandedMed === metric.medication_id && metric.details && (
                    <div className="ml-2 mt-1 p-2 rounded-lg bg-ml-surface-container-low/30 space-y-2 text-[11px]">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <span className="text-ml-on-surface-variant/60">Dosis actual:</span>
                        <span className="text-ml-on-surface">{metric.details.current_dosage ?? '—'}</span>
                        <span className="text-ml-on-surface-variant/60">Frecuencia:</span>
                        <span className="text-ml-on-surface">{metric.details.current_frequency ?? '—'}</span>
                        <span className="text-ml-on-surface-variant/60">Estado:</span>
                        <span className={cn(
                          'capitalize',
                          metric.details.status === 'active' ? 'text-green-600' : 'text-ml-on-surface-variant'
                        )}>
                          {prescriptionStatusLabels[metric.details.status] ?? metric.details.status}
                        </span>
                        <span className="text-ml-on-surface-variant/60">Resurtidos:</span>
                        <span className="text-ml-on-surface">{metric.details.refills_remaining}</span>
                      </div>
                      {metric.details.prescriptions.length > 0 && (
                        <div className="pt-1.5 border-t border-ml-outline-variant/10 space-y-1">
                          <p className="text-[10px] uppercase tracking-wider text-ml-on-surface-variant/50 font-bold">
                            Historial
                          </p>
                          {metric.details.prescriptions.map((p) => (
                            <div key={p.id} className="flex items-center justify-between text-[10px]">
                              <span className="text-ml-on-surface-variant">
                                {p.prescribed_date
                                  ? formatDate(p.prescribed_date)
                                  : '—'}
                              </span>
                              <span className="text-ml-on-surface">{p.dosage} × {p.quantity}</span>
                              <span className={cn(
                                'capitalize px-1.5 py-0.5 rounded',
                                p.status === 'filled' || p.status === 'picked_up'
                                  ? 'bg-green-100 text-green-700'
                                : p.status === 'cancelled'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-ml-surface-container-high text-ml-on-surface-variant'
                              )}>
                                {p.status ? prescriptionStatusLabels[p.status] ?? p.status : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Lab Results */}
      <div
        ref={(node) => {
          sectionRefs.current.labs = node
        }}
      >
        <SectionHeader
          icon={FlaskConical}
          title="Laboratorios"
          count={labResults.length}
          isOpen={openSections.labs}
          onToggle={() => toggleSection('labs')}
        />
        {openSections.labs && (
          <div className="pl-7 pb-2 space-y-1.5">
            {labResults.length === 0 ? (
              <p className="text-xs text-ml-on-surface-variant/60 italic">
                Sin resultados de laboratorio
              </p>
            ) : (
              labResults.slice(0, 10).map((lr: any) => (
                <div key={lr.id} className="p-2 rounded-lg bg-ml-surface-container-low/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-ml-on-surface">
                      {lr.test_name}
                    </span>
                    <span className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded capitalize',
                      lr.status === 'completed' || lr.status === 'reviewed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-ml-surface-container-high text-ml-on-surface-variant'
                    )}>
                      {labStatusLabels[lr.status] ?? lr.status}
                    </span>
                  </div>
                  {lr.result_value && (
                    <p className="text-[11px] text-ml-on-surface-variant mt-0.5">
                      {lr.result_value} {lr.result_unit ?? ''}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Family History */}
      <div
        ref={(node) => {
          sectionRefs.current.family = node
        }}
      >
        <SectionHeader
          icon={Users}
          title="Antecedentes Familiares"
          count={familyHistory.length}
          isOpen={openSections.family}
          onToggle={() => toggleSection('family')}
        />
        {openSections.family && (
          <div className="pl-7 pb-2 space-y-1">
            {familyHistory.length === 0 ? (
              <p className="text-xs text-ml-on-surface-variant/60 italic">
                Sin antecedentes familiares
              </p>
            ) : (
              familyHistory.map((fh, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                  <span className="text-xs text-ml-on-surface">
                    {fh.condition}
                  </span>
                  <span className="text-[10px] text-ml-on-surface-variant/60">
                    ({fh.relation})
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
